import React, { FC, Fragment, memo } from "react";
import {
	Draggable,
	DraggableProvided,
	DraggableStateSnapshot
} from "react-beautiful-dnd";
import ZTooltip from "@atoms/ZTooltip";
import ZTypography from "@atoms/ZTypography";
import "./index.less";
import { IDraggableCard } from "./interface";
import { EFontWeights } from "@atoms/ZTypography/interface";
import { EIcons } from "@atoms/ZIcon/interface";
import { EColors } from "@atoms/ZColors/interface";
import ZIcon from "@atoms/ZIcon";
import ZMenu from "@molecules/ZMenu";
import ZDropdown from "@molecules/ZDropdown";
import ZMoreButton from "@molecules/ZMoreButton";
import {
	capitalizeAfterUnderscore,
	customDateFormatter
} from "@/Utils";

import {
	getOpportunityCardColorCodeClass,
	getDisplayNameBasedOnShareStatus,
	getIconPropsBasedOnShareStatus,
	getTooltipTextBasedOnShareStatus
} from "@/Utils/Opportunity";
import { Col, Row } from "antd/lib/grid";
import ZPipelineProgressPopover from "@molecules/ZPipelineProgressPopover";
import { OpportunityShareStatus } from "@/Contracts/Opportunity";

const { ZText } = ZTypography;

const ZDraggableCard: FC<IDraggableCard> = ({
	isDragDisabled,
	opportunityId,
	opportunityName,
	onOpportunityNameClick,
	clientCompanyName,
	isClientAssigned,
	onClientCompanyNameClick,
	estimatedCloseDate,
	closeDate,
	isTerminalStage,
	amountTypeToShowInOppCard,
	valueToShowInOppCard,
	datePattern,
	index,
	actions,
	addActivity,
	overdueActivity,
	upcomingActivity,
	opportunityShareStatus,
	opportunityInsights,
	sharerName,
	opportunitySharedAt
}) => {
	return (
		<Draggable
			draggableId={opportunityId}
			index={index}
			isDragDisabled={isDragDisabled}
		>
			{(
				provided: DraggableProvided,
				snapshot: DraggableStateSnapshot
			) => (
				<div
					ref={provided.innerRef}
					{...provided.draggableProps}
					{...provided.dragHandleProps}
					className={`opportunity-card border-l-1 ${
						snapshot.isDragging ? "is-dragging" : ""
					}`}
				>
					<div
						className={`opportunity-card-container rounded bg-gray-1 transition-all origin-left mb-2 border border-solid border-gray-3 border-l-4 ${getOpportunityCardColorCodeClass(
							opportunityShareStatus
						)}`}
					>
						<Row className="opportunity-title-client-container flex justify-between p-2 mb-1">
							<Col span={21}>
								<div
									data-test-opportunity-created={`${opportunityId}`}
									className="inline-block cursor-pointer pb-2 w-full"
									onClick={onOpportunityNameClick}
								>
									<ZTooltip
										placement="topLeft"
										title={opportunityName}
									>
										<ZText weight={EFontWeights.MEDIUM}>
											{opportunityName}
										</ZText>
									</ZTooltip>
								</div>

								<ZTooltip
									placement="topLeft"
									title={`${clientCompanyName} ${
										!isClientAssigned
											? "(Don't have access to this client)"
											: ""
									}`}
								>
									{isClientAssigned ? (
										<ZText
											className="cursor-pointer"
											onClick={() =>
												onClientCompanyNameClick?.()
											}
										>
											{clientCompanyName}
										</ZText>
									) : (
										<ZText>{clientCompanyName}</ZText>
									)}
								</ZTooltip>
							</Col>
							{!!actions?.length && (
								<Col span={3}>
									<ZMoreButton
										className="mt-1 mr-n1"
										showMoreText={false}
										menu={
											<ZMenu>
												{actions.map(
													(action, index) => (
														<ZMenu.Item
															key={index}
															onClick={
																action.onClick
															}
															danger={
																action.danger
															}
														>
															{action.name}
														</ZMenu.Item>
													)
												)}
											</ZMenu>
										}
									/>
								</Col>
							)}
						</Row>
						<div className="flex items-center justify-between">
							<ZTooltip
								placement="topLeft"
								title={capitalizeAfterUnderscore(
									amountTypeToShowInOppCard
								)}
							>
								<ZText
									weight={EFontWeights.MEDIUM}
									level={1}
									className="pl-2 pr-6 truncate"
								>
									{valueToShowInOppCard}
								</ZText>
							</ZTooltip>
							{estimatedCloseDate ? (
								<ZTooltip
									placement="topLeft"
									title={
										isTerminalStage
											? "Close Date"
											: "Expected Close Date"
									}
								>
									<div className="pr-2">
										{isTerminalStage && (
											<ZText colorVariant={7}>
												{closeDate
													? customDateFormatter(
															closeDate as Date,
															datePattern
													  )
													: "--"}
											</ZText>
										)}
										{!isTerminalStage && (
											<ZText
												colorVariant={7}
												className="whitespace-nowrap"
											>
												{estimatedCloseDate
													? customDateFormatter(
															estimatedCloseDate as Date,
															datePattern
													  )
													: "--"}
											</ZText>
										)}
									</div>
								</ZTooltip>
							) : (
								<div />
							)}
						</div>
						<Row className="p-2" justify="space-between">
							<Col>
								{opportunityShareStatus !==
									OpportunityShareStatus.SELF && (
									<ZPipelineProgressPopover
										opportunityInsights={
											opportunityInsights
										}
										sharerName={sharerName ?? ""}
									>
										<div
											className={`flex items-center shared-status-block ${getOpportunityCardColorCodeClass(
												opportunityShareStatus
											)}`}
										>
											<ZTooltip
												title={getTooltipTextBasedOnShareStatus(
													opportunityShareStatus,
													opportunitySharedAt
														? customDateFormatter(
																new Date(
																	opportunitySharedAt
																),
																datePattern
														  )
														: undefined
												)}
												showtooltip={
													!(
														opportunityShareStatus ===
															OpportunityShareStatus.ACCEPTED ||
														opportunityShareStatus ===
															OpportunityShareStatus.SHARED_BY_PARTNER
													)
												}
												placement="topLeft"
											>
												<div className="flex items-center">
													<ZIcon
														{...getIconPropsBasedOnShareStatus(
															opportunityShareStatus
														)}
														colorVariant={6}
														className="mr-1"
														size="sm"
													/>
													<ZText>
														{getDisplayNameBasedOnShareStatus(
															opportunityShareStatus
														)}
													</ZText>
												</div>
											</ZTooltip>
										</div>
									</ZPipelineProgressPopover>
								)}
							</Col>
							<Col className="items-end text-right">
								<Fragment>
									{overdueActivity?.shouldDisplayRedWarnIcon && (
										<ZTooltip
											placement="topLeft"
											title="Overdue activity"
										>
											<div
												className="cursor-pointer flex items-center"
												onClick={
													overdueActivity.showOverdueTasksDrawer
												}
											>
												<ZText className="text-red-6 mr-1">
													Overdue tasks
												</ZText>
												<ZIcon
													name={EIcons.WARNINGCIRCLE}
													color={EColors.RED}
													colorVariant={6}
													size="sm"
												/>
											</div>
										</ZTooltip>
									)}
									{upcomingActivity?.shouldDisplayBlueUpcomingIcon && (
										<ZTooltip
											placement="topLeft"
											title="Upcoming activity"
										>
											<div
												className="cursor-pointer flex items-center"
												onClick={
													upcomingActivity.showUpcomingActivitiesDrawer
												}
											>
												<ZText
													color={EColors.BLUE}
													colorVariant={6}
													className="mr-1"
												>
													Upcoming tasks
												</ZText>
												<ZIcon
													name={EIcons.HISTORY}
													color={EColors.BLUE}
													colorVariant={6}
													size="sm"
												/>
											</div>
										</ZTooltip>
									)}
									{addActivity?.isAddActivityAccessible &&
										addActivity.shouldDisplayYellowWarnIcon && (
											<ZTooltip
												placement="topLeft"
												title="No activity scheduled"
											>
												<ZDropdown
													trigger={["click"]}
													overlay={
														<ZMenu>
															{addActivity.addActivityActions?.map(
																action => (
																	<ZMenu.Item
																		key={
																			action.name
																		}
																		onClick={
																			action.onClick
																		}
																		danger={
																			action.danger
																		}
																	>
																		{
																			action.name
																		}
																	</ZMenu.Item>
																)
															)}
														</ZMenu>
													}
												>
													<div
														className="cursor-pointer flex items-center"
														onClick={
															addActivity.handleAddActivityClick
														}
													>
														<ZText
															color={EColors.GOLD}
															colorVariant={6}
															className="mr-1"
														>
															Add activity
														</ZText>
														<ZIcon
															name={
																EIcons.WARNINGCIRCLE
															}
															color={EColors.GOLD}
															colorVariant={6}
															size="sm"
														/>
													</div>
												</ZDropdown>
											</ZTooltip>
										)}
								</Fragment>
							</Col>
						</Row>
					</div>
				</div>
			)}
		</Draggable>
	);
};

export default memo(ZDraggableCard);
